import { Separator } from '@/components/ui/separator'

export default function FaqSection() {
  return (
    <>
      <section id="faq-general" aria-labelledby="faq-general-heading">
        <h3 id="faq-general-heading" className="font-semibold mb-3">
          General Questions
        </h3>
        <dl className="space-y-4">
          <div>
            <dt className="text-sm font-medium mb-1">What does the My/All toggle do?</dt>
            <dd className="text-sm text-muted-foreground">
              List pages default to showing only records assigned to you ("My" view). Switching to
              "All" shows every record in the system. This applies to Accounts, Contacts, Leads,
              Opportunities, Tasks, and Events. Your preference does not persist — it resets each
              time you visit the page.
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium mb-1">Can I undo a deletion?</dt>
            <dd className="text-sm text-muted-foreground">
              Grackle CRM uses soft delete — when you delete a record, it is marked as inactive
              rather than permanently removed. Contact your administrator if you need a record
              restored.
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium mb-1">How do I change my password?</dt>
            <dd className="text-sm text-muted-foreground">
              Go to Settings from the sidebar or your user menu. If your account uses OAuth (Google
              sign-in), your password is managed through your Google account.
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium mb-1">Who can see my records?</dt>
            <dd className="text-sm text-muted-foreground">
              All authenticated users can view records across the CRM when using the "All" toggle.
              Ownership determines what shows in your default "My" view and is used for task
              assignments and reporting.
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium mb-1">How do I convert a lead?</dt>
            <dd className="text-sm text-muted-foreground">
              Open the lead detail page and click the <span className="font-medium">Convert</span>{' '}
              button. This creates an Account, Contact, and Opportunity in one step. See the{' '}
              <a
                href="#leads"
                className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
              >
                Leads
              </a>{' '}
              section for details.
            </dd>
          </div>
        </dl>
      </section>
      <Separator />

      <section id="faq-troubleshooting" aria-labelledby="faq-troubleshooting-heading">
        <h3 id="faq-troubleshooting-heading" className="font-semibold mb-3">
          Troubleshooting
        </h3>
        <dl className="space-y-4">
          <div>
            <dt className="text-sm font-medium mb-1">My records are not showing up</dt>
            <dd className="text-sm text-muted-foreground">
              Check the <span className="font-medium">My / All</span> toggle at the top of the list.
              If it is set to "My," you will only see records assigned to you. Switch to "All" to
              see everything. Also check any active filters — clear them to see the full list.
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium mb-1">I cannot edit a record</dt>
            <dd className="text-sm text-muted-foreground">
              If you see an amber banner at the top of the page, you are in{' '}
              <span className="font-medium">impersonation mode</span>, which is read-only. Click
              "Exit Impersonation" to return to your own account. If there is no banner, your user
              role may not have edit permissions — check with your administrator.
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium mb-1">The Export button is missing</dt>
            <dd className="text-sm text-muted-foreground">
              Export is an Admin and Super Admin feature. Standard users and Support users do not
              see the export button. It is also hidden during impersonation of a non-admin user. If
              you need data exported, ask your administrator.
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium mb-1">Google Calendar is not syncing</dt>
            <dd className="text-sm text-muted-foreground">
              Go to <span className="font-medium">Settings &rarr; Google Integration</span> and
              check your connection status. If the connection shows as disconnected, click to
              reconnect and re-authorize. Make sure you are using the same Google account as before.
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium mb-1">
              I see a "new record" but I did not create it
            </dt>
            <dd className="text-sm text-muted-foreground">
              Records can be created by web form webhooks, lead imports, or other team members.
              Check the record's Created By field and creation date for context.
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium mb-1">The page is not loading or seems stuck</dt>
            <dd className="text-sm text-muted-foreground">
              Try refreshing your browser. If the issue persists, clear your browser cache or try an
              incognito/private window. If the problem continues, contact your administrator.
            </dd>
          </div>
        </dl>
      </section>
    </>
  )
}
